import { z } from 'zod'

import { ForManagingDApps } from '../../ports'
import {
	ApplicationError,
	type DApp,
	ErrorType,
	type Template,
	type UseCase
} from '../types'
import { BackendErrors, GuardInput } from '../utils'
import { GitHubUrl } from '../value_object'

export class DAppService implements ForManagingDApps {
	private readonly _dAppManager: ForManagingDApps
	private readonly _defaultErrorCode = 'dapp.unknown'

	constructor(dAppManager: ForManagingDApps) {
		this._dAppManager = dAppManager
	}

	static ValidateDAppName(
		name: string,
		dapps: DApp[]
	): ApplicationError | void {
		if (!name) return

		const isNameAlreadyInUse = dapps.find(d => d.name === name)

		if (isNameAlreadyInUse) {
			return new ApplicationError({
				type: ErrorType.InvalidInput,
				message: 'The dApp name is already in use',
				inputErrors: {
					name: 'The dApp name is already in use'
				},
				code: 'dapp_name_in_use'
			})
		}

		return GuardInput.check(ForManagingDApps.CreateDAppBasePayload.partial(), {
			name
		})
	}

	static async ValidateGitHubUrl(githubUrl: string) {
		return await GitHubUrl.getSHAFromLatestCommit(githubUrl)
	}

	getTemplates(): Promise<{ data?: Template[]; error?: ApplicationError }> {
		return this._dAppManager.getTemplates()
	}

	getUseCases(): Promise<{ data?: UseCase[]; error?: ApplicationError }> {
		return this._dAppManager.getUseCases()
	}

	async createDAppFromTemplate(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromTemplatePayload>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const payloadError = GuardInput.check(
			ForManagingDApps.CreateDAppFromTemplatePayload,
			payload
		)
		if (payloadError) return { error: payloadError }

		const { data, error } =
			await this._dAppManager.createDAppFromTemplate(payload)

		if (error) {
			return {
				error: BackendErrors.check(error, this._defaultErrorCode)
			}
		}

		return {
			data: {
				...data!,
				projectId: payload.publicProjectId
			}
		}
	}

	async createDAppFromPrompt(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromPromptPayload>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const payloadError = GuardInput.check(
			ForManagingDApps.CreateDAppFromPromptPayload,
			payload
		)
		if (payloadError) return { error: payloadError }

		const { data, error } =
			await this._dAppManager.createDAppFromPrompt(payload)

		if (error) {
			return {
				error: BackendErrors.check(error, this._defaultErrorCode)
			}
		}

		return {
			data: {
				...data!,
				projectId: payload.publicProjectId
			}
		}
	}

	async createDAppFromGithub(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromGithubPayload>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const payloadError = GuardInput.check(
			ForManagingDApps.CreateDAppFromGithubPayload,
			payload
		)
		if (payloadError) return { error: payloadError }

		const githubResult = await DAppService.ValidateGitHubUrl(payload.githubUrl)

		if (githubResult.error) {
			return { error: githubResult.error }
		}

		const adaptedPayload = {
			name: payload.name,
			description: payload.description,
			githubUrl: payload.githubUrl,
			githubRepoLatestCommit: githubResult.shaLatestCommit,
			publicProjectId: payload.publicProjectId
		}

		const { data, error } =
			await this._dAppManager.createDAppFromGithub(adaptedPayload)

		if (error) {
			return {
				error: BackendErrors.check(error, this._defaultErrorCode)
			}
		}

		return {
			data: {
				...data!,
				projectId: payload.publicProjectId
			}
		}
	}

	openWorkspace(dappId: string): Promise<{ error?: ApplicationError }> {
		return this._dAppManager.openWorkspace(dappId)
	}

	isWorkspaceHealthy(
		workspaceUrl: string
	): Promise<{ data?: boolean; error?: ApplicationError }> {
		return this._dAppManager.isWorkspaceHealthy(workspaceUrl)
	}
}
